/**
 * Página de Monitoramento da Fila PGMQ (Flow Engine 3.0)
 * 
 * Dashboard completo para visualização de estatísticas da fila PGMQ
 * incluindo métricas, gráficos de volume, distribuição e alertas de saúde.
 */

import { useState } from 'react';
import { RefreshCw, Database, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQueryClient } from '@tanstack/react-query';
import { PgmqStatusCards } from '@/components/dashboard/PgmqStatusCards';
import { PgmqVolumeChart } from '@/components/dashboard/PgmqVolumeChart';
import { PgmqActionDistribution } from '@/components/dashboard/PgmqActionDistribution';
import { PgmqProviderDistribution } from '@/components/dashboard/PgmqProviderDistribution';
import { PgmqHealthAlerts } from '@/components/dashboard/PgmqHealthAlerts';
import { PgmqInfoCard } from '@/components/dashboard/PgmqInfoCard';

export default function PgmqMonitor() {
  const queryClient = useQueryClient();
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Função para atualizar todos os dados
  const handleRefresh = async () => {
    setIsRefreshing(true);
    
    // Invalida todas as queries relacionadas ao PGMQ
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['flow-queue-stats-pgmq'] }),
      queryClient.invalidateQueries({ queryKey: ['flow-queue-volume-pgmq'] }),
      queryClient.invalidateQueries({ queryKey: ['flow-queue-by-action-pgmq'] }),
      queryClient.invalidateQueries({ queryKey: ['flow-queue-by-provider-pgmq'] }),
      queryClient.invalidateQueries({ queryKey: ['flow-queue-alerts-pgmq'] }),
    ]);
    
    setLastUpdate(new Date());
    setIsRefreshing(false);
  };

  // Formatar tempo desde última atualização
  const getTimeSinceUpdate = () => {
    const seconds = Math.floor((new Date().getTime() - lastUpdate.getTime()) / 1000);
    if (seconds < 60) return `há ${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `há ${minutes}min`;
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Fila PGMQ</h1>
            <p className="text-muted-foreground">
              Flow Engine 3.0 - Monitoramento em tempo real
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>Atualizado {getTimeSinceUpdate()}</span>
          </div>
          <Button 
            onClick={handleRefresh} 
            disabled={isRefreshing}
            variant="outline"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Cards de Status */}
      <PgmqStatusCards />

      {/* Alertas de Saúde */}
      <PgmqHealthAlerts />

      {/* Gráficos - Linha 1 */}
      <div className="grid gap-6 md:grid-cols-2">
        <PgmqVolumeChart />
        <PgmqActionDistribution />
      </div>

      {/* Gráficos - Linha 2 */}
      <div className="grid gap-6 md:grid-cols-2">
        <PgmqProviderDistribution />
        <PgmqInfoCard />
      </div>
    </div>
  );
}
